import { useCallback, useEffect, useState } from 'react';
import type { SsoUser } from '@domain/entities';
import type { InviteUserPayload, ResetPasswordResult } from '@application/dto';
import {
  getInviteUserUseCase,
  getListUsersUseCase,
  getResetUserPasswordUseCase,
  getToggleUserStatusUseCase,
} from '@di';
import { describeError } from '@shared/utils';

export function useUsers(search: string, pageSize = 20) {
  const [users, setUsers] = useState<SsoUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getListUsersUseCase().execute({
        page: 1,
        limit: pageSize,
        search: search || undefined,
      });
      setUsers(result.data);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setIsLoading(false);
    }
  }, [search, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = useCallback(
    async (payload: InviteUserPayload) => {
      setError(null);
      try {
        await getInviteUserUseCase().execute(payload);
        await load();
        return true;
      } catch (cause) {
        setError(describeError(cause));
        return false;
      }
    },
    [load]
  );

  const toggleStatus = useCallback(
    async (user: SsoUser) => {
      setError(null);
      try {
        await getToggleUserStatusUseCase().execute(user);
        await load();
      } catch (cause) {
        setError(describeError(cause));
      }
    },
    [load]
  );

  const resetPassword = useCallback(
    async (user: SsoUser): Promise<ResetPasswordResult | null> => {
      setError(null);
      try {
        return await getResetUserPasswordUseCase().execute(user);
      } catch (cause) {
        setError(describeError(cause));
        return null;
      }
    },
    []
  );

  return { users, isLoading, error, reload: load, invite, toggleStatus, resetPassword };
}
